import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Logo_gopaperless from "../../assets/images/logos/paperless/gopaperless-logo-darkblue-2.png";

// ---------- Helpers ----------

// Xác định theme sớm (giống script trong <head> của index.html)
const getInitialTheme = () => {
  const params = new URLSearchParams(window.location.search);
  const p = (params.get("theme") || "").toLowerCase();
  const isValid = p === "dark" || p === "light";

  try {
    if (isValid) {
      localStorage.setItem("theme", p);
      return p;
    }
    const saved = localStorage.getItem("theme");
    if (saved === "dark" || saved === "light") return saved;
  } catch (e) {
    if (isValid) return p;
  }

  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
};

// Xác định ngôn ngữ (giống index.html)
const detectLang = () => {
  const params = new URLSearchParams(window.location.search);
  let lang = "en";
  try {
    lang = localStorage.getItem("lang") || "";
  } catch (e) {
    lang = "";
  }
  if (!lang) {
    lang = (
      params.get("ui_locales") ||
      params.get("hl") ||
      params.get("lang") ||
      "en"
    ).toLowerCase();
  }
  return lang.startsWith("vi") ? "vi" : "en";
};

// Link điều khoản / chính sách theo ngôn ngữ
const POLICY_URLS = {
  en: {
    term: "https://gopaperless.mobile-id.vn/en/uploads/GoPaperless-DieuKhoanVaDieuKienSuDung",
    privacy:
      "https://gopaperless.mobile-id.vn/en/uploads/GoPaperless-InformationSecurityPolicy",
  },
  vi: {
    term: "https://gopaperless.mobile-id.vn/vi/uploads/GoPaperless-DieuKhoanVaDieuKienSuDung",
    privacy:
      "https://gopaperless.mobile-id.vn/vi/uploads/GoPaperless-InformationSecurityPolicy",
  },
};

const buildDeepLink = (code, state) =>
  `gopaperless-agent://callback?code=${encodeURIComponent(
    code,
  )}&state=${encodeURIComponent(state)}`;

// ---------- CSS (theme bằng CSS variables, scope trong .gpl-deeplink) ----------
const css = `
.gpl-deeplink {
  --bg: #ffffff;
  --text-muted: #6c757d;
  --link: #2563eb;
  --separator: #cccccc;
  --btn-bg: #ffffff;
  --btn-color: #0033ad;
  --btn-border: #0033ad;
  --btn-hover-bg: #fbc70e;
  --btn-hover-color: #0033ad;
  --logo-filter: none;
  color-scheme: light;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  text-align: center;
  gap: 16px;
  background-color: var(--bg);
  transition: background-color 0.2s ease;
}

.gpl-deeplink[data-theme="dark"] {
  --bg: #0f172a;
  --text-muted: #9aa4b2;
  --link: #8ab4ff;
  --separator: #475569;
  --btn-bg: transparent;
  --btn-color: #8ab4ff;
  --btn-border: #8ab4ff;
  --btn-hover-bg: #fbc70e;
  --btn-hover-color: #0033ad;
  /* Logo xanh đậm -> trắng trên nền tối. Nếu có logo bản trắng thì đổi src và bỏ filter này. */
  --logo-filter: brightness(0) invert(1);
  color-scheme: dark;
}

.gpl-deeplink,
.gpl-deeplink * {
  font-family: "Montserrat", sans-serif !important;
}

.gpl-deeplink .gpl-logo {
  max-width: 250px;
  height: auto;
  display: block;
  margin-bottom: 16px;
  filter: var(--logo-filter);
}

.gpl-deeplink .gpl-launch {
  padding: 0 24px;
  line-height: 50px;
  text-align: center;
  text-decoration: none;
  font-size: 16px;
  font-weight: 400;
  border-radius: 30px;
  border: 2px solid var(--btn-border);
  color: var(--btn-color);
  background-color: var(--btn-bg);
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.gpl-deeplink .gpl-launch:hover {
  background-color: var(--btn-hover-bg);
  border: 2px solid var(--btn-hover-bg);
  border-radius: 30px;
  color: var(--btn-hover-color);
  text-decoration: none;
}

.gpl-deeplink .gpl-hint {
  font-size: 14px;
  color: var(--text-muted);
  margin: 0;
}

.gpl-deeplink .gpl-hint a,
.gpl-deeplink .gpl-footer a {
  color: var(--link);
  text-decoration: none;
}

.gpl-deeplink .gpl-footer {
  position: absolute;
  bottom: 16px;
  left: 0;
  width: 100%;
  font-size: 14px;
  color: var(--text-muted);
  text-align: center;
  /* Ghi đè CSS global của app đang style thẻ <footer> (nền sáng, viền, padding) */
  background: transparent !important;
  border: none !important;
  padding: 0 !important;
  margin: 0;
  height: auto;
}

.gpl-deeplink .gpl-sep {
  color: var(--separator);
}
`;

// ---------- Component ----------

const DeeplinkContent = () => {
  const { t, i18n } = useTranslation();

  const [theme, setTheme] = useState(getInitialTheme);
  const [lang] = useState(detectLang);

  const params = new URLSearchParams(window.location.search);
  const code = params.get("code") || "UNKNOWN_CODE";

  const urls = POLICY_URLS[lang];

  // Font Montserrat (index.html load qua <link>; ở React tự thêm nếu app chưa có)
  useEffect(() => {
    const href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap";
    if (document.querySelector(`link[href="${href}"]`)) return;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }, []);

  // Đồng bộ ngôn ngữ i18n + <html lang> + document.title
  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    document.title = t("gpl_agent.title");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, i18n.language]);

  // Nếu chưa có ?theme= và chưa lưu -> tự đổi theo hệ thống khi người dùng đổi
  useEffect(() => {
    if (!window.matchMedia) return;

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const hasExplicitTheme = () => {
      try {
        return !!localStorage.getItem("theme");
      } catch (e) {
        return !!new URLSearchParams(window.location.search).get("theme");
      }
    };
    const onChange = (e) => {
      if (!hasExplicitTheme()) setTheme(e.matches ? "dark" : "light");
    };

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
    };
  }, []);

  // Auto redirect: PREPARE_WEB_LOGIN sau 400ms
  useEffect(() => {
    console.log("[DEEP LINK] Page loaded at:", new Date().toISOString());

    const autoDeepLink = buildDeepLink(code, "PREPARE_WEB_LOGIN");
    console.log(
      "[DEEP LINK] Scheduled auto redirect at:",
      new Date().toISOString(),
      " -> will redirect in 400ms",
    );

    const timer = setTimeout(() => {
      console.log("[DEEP LINK] Auto redirect executing:", autoDeepLink);
      window.location.href = autoDeepLink; // Chrome sẽ hiện hộp thoại xác nhận ở đây
    }, 400);

    return () => clearTimeout(timer);
  }, [code]);

  // Nút Launch + link thủ công => WEB_LOGIN
  const webLoginLink = buildDeepLink(code, "WEB_LOGIN");

  const handleLaunch = () => {
    console.log("[DEEP LINK] Launch button ->", webLoginLink);
    window.location.href = webLoginLink;
  };

  return (
    <div id="content" className="gpl-deeplink" data-theme={theme}>
      <style>{css}</style>

      <img
        id="logo"
        className="gpl-logo"
        src={Logo_gopaperless}
        alt="GoPaperless Logo"
      />

      <button id="launchButton" className="gpl-launch" onClick={handleLaunch}>
        {t("gpl_agent.title")}
      </button>

      <p className="gpl-hint">
        <span id="notLaunchingText">{t("gpl_agent.notLaunching")}</span>{" "}
        <a id="manualLink" href={webLoginLink}>
          {t("gpl_agent.clickHere")}
        </a>
      </p>

      <footer className="gpl-footer">
        <a id="term" href={urls.term} target="_blank" rel="noopener noreferrer">
          {t("gpl_agent.terms")}
        </a>
        <span className="gpl-sep"> | </span>
        <a
          id="privacy"
          href={urls.privacy}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("gpl_agent.privacy")}
        </a>
      </footer>
    </div>
  );
};

export default DeeplinkContent;
